(() => {
  const number = value => value !== null && value !== undefined && String(value).trim() !== '' && Number.isFinite(Number(value)) ? Number(value) : null;
  const money = value => new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(value);
  const payment = (price, down, apr, term) => {
    if ([price, down, apr, term].some(v => !Number.isFinite(v)) || price < 0 || down < 0 || down > price || apr < 0 || apr > 100 || term < 1 || term > 360 || !Number.isInteger(term)) return null;
    const principal = Math.round((price - down) * 100) / 100;
    const rate = apr / 1200;
    const monthly = principal === 0 ? 0 : rate === 0 ? principal / term : principal * rate / -Math.expm1(-term * Math.log1p(rate));
    return {principal, monthly, total: monthly * term, interest: Math.max(0, monthly * term - principal)};
  };
  const miles = (a, b) => {
    const rad = value => value * Math.PI / 180;
    const lat = rad(b.lat - a.lat), lon = rad(b.lon - a.lon);
    const h = Math.sin(lat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(lon / 2) ** 2;
    return 3958.7613 * 2 * Math.asin(Math.sqrt(Math.min(1, h)));
  };
  const zoneFor = (zip, zones) => zones.filter(z => /^\d{1,5}$/.test(String(z.prefix)) && zip.startsWith(String(z.prefix))).sort((a,b) => String(b.prefix).length - String(a.prefix).length)[0];
  const quoteZone = (z,c) => {
    if (!z) return null;
    const distance = number(z.miles), max = number(c.maximum);
    if (distance !== null && (distance < 0 || (max !== null && distance > max))) return null;
    if (z.freeEligible === true && distance !== null && distance <= Number(c.freeMiles)) return 0;
    const fixed = number(z.price), rate = number(c.rate);
    let charge = fixed !== null ? fixed : distance !== null && rate !== null && rate >= 0 ? distance * rate : null;
    if (charge === null || charge < 0) return null;
    charge = Math.max(charge, Number(c.minimum) || 0);
    if (c.applyPromo === true) charge *= 1 - Math.min(50, Math.max(0, Number(c.promo) || 0)) / 100;
    return Math.round(charge * 100) / 100;
  };
  if (typeof module !== 'undefined') module.exports = {number, payment, miles, zoneFor, quoteZone};
  if (typeof customElements === 'undefined' || customElements.get('ncc-planning-tools')) return;
  customElements.define('ncc-planning-tools', class extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.q = name => this.querySelector('[data-ncc-' + name + ']');
      this.t = JSON.parse(this.q('tool-strings').textContent);
      this.c = JSON.parse(this.q('tool-config').textContent);
      this.dialog = this.querySelector('dialog');
      this.q('down').value = Math.max(0, Number(this.c.down) || 0);
      this.q('apr').value = this.c.apr ?? '';
      this.q('lender').textContent = this.c.lender;
      this.q('offer').querySelector('[value=denago]').disabled = this.c.denagoEnabled === false;
      this.setTerms();
      this.addEventListener('input', e => {if (!e.target.matches('[data-ncc-zip]')) this.calculate();});
      this.q('offer').addEventListener('change', () => {this.setTerms(); this.calculate();});
      this.q('term').addEventListener('change', () => this.calculate());
      this.q('close').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => {this.requestId = (this.requestId || 0) + 1; this.q('check').disabled = false; this.opener?.focus();});
      this.dialog.addEventListener('click', e => {if (e.target === this.dialog) {const r = this.dialog.getBoundingClientRect(); if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.dialog.close();}});
      this.querySelectorAll('[data-ncc-tab]').forEach(tab => {
        tab.addEventListener('click', () => this.selectTab(tab.dataset.nccTab));
        tab.addEventListener('keydown', e => {const tabs = [...this.querySelectorAll('[data-ncc-tab]')]; let i = tabs.indexOf(tab); if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') i = (i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length; else if (e.key === 'Home') i = 0; else if (e.key === 'End') i = tabs.length - 1; else return; e.preventDefault(); this.selectTab(tabs[i].dataset.nccTab); tabs[i].focus();});
      });
      document.addEventListener('click', e => {const link = e.target.closest('[data-ncc-open]'); if (!link) return; e.preventDefault(); this.open(link);});
      this.q('check').addEventListener('click', () => this.delivery());
      this.q('zip').addEventListener('keydown', e => {if (e.key === 'Enter') {e.preventDefault(); this.delivery();}});
      this.q('zip').addEventListener('input', () => {this.requestId = (this.requestId || 0) + 1; this.q('delivery-result').replaceChildren(); this.q('check').disabled = false;});
      this.calculate();
    }
    setTerms() {
      const promo = this.q('offer').value === 'denago';
      if (promo) {if (this.lastOffer !== 'denago') this.standardApr = this.q('apr').value; this.q('apr').value = '0';}
      else this.q('apr').value = this.standardApr ?? this.c.apr ?? '';
      this.q('apr').readOnly = promo;
      this.lastOffer = promo ? 'denago' : 'standard';
      const terms = [...new Set(String(promo ? this.c.denagoTerms : this.c.terms).split(',').map(s => Number(s.trim())).filter(v => Number.isInteger(v) && v > 0 && v <= 360))];
      this.q('term').replaceChildren(...terms.map(term => {const o = document.createElement('option'); o.value = term; o.textContent = term + ' ' + this.t.months; return o;}));
    }
    selectTab(name) {
      this.querySelectorAll('[data-ncc-tab]').forEach(el => {const active = el.dataset.nccTab === name; el.setAttribute('aria-selected', String(active)); el.tabIndex = active ? 0 : -1;});
      this.querySelectorAll('[data-ncc-panel]').forEach(el => el.hidden = el.dataset.nccPanel !== name);
    }
    open(link) {
      this.opener = link;
      let cents = number(link.dataset.nccPrice);
      const context = link.closest('product-info');
      if (context && !link.hasAttribute('data-ncc-builder')) {
        const selected = context.querySelector('input[name=id]')?.value;
        const json = context.querySelector('[data-selected-variant]');
        try {const variant = json && JSON.parse(json.textContent); if (variant && String(variant.id) === selected) cents = number(variant.price);} catch {}
      }
      this.q('price').value = cents === null ? '' : (cents / 100).toFixed(2);
      this.q('context').textContent = link.dataset.nccTitle || this.t.heading_price;
      this.q('partial').hidden = link.dataset.nccPartial !== 'true';
      const title = (link.dataset.nccTitle || '').toLowerCase();
      const yamaha = title.includes('yamaha') || link.hasAttribute('data-ncc-builder');
      this.q('offer').querySelector('[value=denago]').disabled = yamaha || (link.hasAttribute('data-ncc-product') && !title.includes('denago')) || this.c.denagoEnabled === false;
      const eligible = title.includes('denago') && /\bnew\b/.test(title) && !title.includes('used') && !title.includes('pre-owned') && !title.includes('refurbished');
      this.q('offer').value = eligible && this.c.denagoEnabled !== false ? 'denago' : 'standard';
      this.setTerms();
      this.selectTab(link.dataset.nccOpen || 'payment');
      this.calculate();
      if (!this.dialog.open) this.dialog.showModal();
      this.querySelector('[aria-selected=true]').focus();
    }
    calculate() {
      const values = ['price','down','apr','term'].map(k => number(this.q(k).value));
      const result = values.some(v => v === null) ? null : payment(...values);
      const out = this.q('payment-result'); out.replaceChildren();
      if (!result) {out.textContent = values[2] === null ? this.t.no_apr : this.t.valid; return;}
      const label = document.createElement('span'); label.textContent = this.t.monthly;
      const value = document.createElement('strong'); value.textContent = money(result.monthly);
      out.append(label, value);
      for (const [key, amount] of [['financed', result.principal], ['interest', result.interest], ['total', result.total]]) {const p = document.createElement('p'); p.textContent = this.t[key] + ': ' + money(amount); out.append(p);}
    }
    async lookup(zip) {
      this.cache ??= new Map(); if (this.cache.has(zip)) return this.cache.get(zip);
      const r = await fetch('https://api.zippopotam.us/us/' + zip, {signal: AbortSignal.timeout(10000), credentials: 'omit', referrerPolicy: 'no-referrer'});
      if (!r.ok) throw Error('ZIP unavailable'); const j = await r.json(), p = j.places?.[0];
      const result = {lat: number(p?.latitude), lon: number(p?.longitude)};
      if (result.lat === null || result.lon === null) throw Error('Invalid ZIP location'); this.cache.set(zip, result); return result;
    }
    async delivery() {
      const zip = this.q('zip').value.trim(), out = this.q('delivery-result');
      const version = this.requestId = (this.requestId || 0) + 1; out.replaceChildren();
      if (!/^\d{5}$/.test(zip)) {out.textContent = this.t.zip_error; return;}
      this.q('check').disabled = true;
      try {
        let zones = []; try {zones = JSON.parse(this.c.zones || '[]'); if (!Array.isArray(zones)) zones = [];} catch {}
        const zone = zoneFor(zip, zones), charge = quoteZone(zone, this.c);
        if (zone && number(zone.miles) !== null) {out.textContent = this.t.distance + ': ' + zone.miles + ' ' + this.t.mile + '. ' + (charge === null ? this.t.pending : this.t.rate + ': ' + money(charge));}
        else {
          const [origin, destination] = await Promise.all([this.lookup(this.c.originZip), this.lookup(zip)]);
          if (version !== this.requestId) return;
          const distance = miles(origin, destination);
          out.textContent = this.t.distance + ': ' + Math.round(distance) + ' ' + this.t.mile + '. ' + (distance <= Number(this.c.freeMiles) ? this.t.near : this.t.far);
        }
        const p = document.createElement('p'); p.textContent = this.t.promotion; out.append(p);
        this.q('delivery-contact').href = '/pages/contact?delivery_zip=' + zip;
      } catch {if (version === this.requestId) out.textContent = this.t.zip_failed;}
      finally {if (version === this.requestId) this.q('check').disabled = false;}
    }
  });
})();

